import { CartLinesUpdateEvent } from '@shopify/events';

const hoverQuery = window.matchMedia('(hover: hover) and (min-width: 750px)');
const canHover = () => hoverQuery.matches;
const clamp = (v, min, max) => Math.min(max, Math.max(min, v));
const tiltFor = (ratio) => `${((Math.acos(ratio) * 180) / Math.PI).toFixed(2)}deg`;

class RaawRack {
  constructor(root) {
    this.root = root;
    this.board = root.querySelector('.raaw-rack__board');
    this.row = root.querySelector('[data-rack-row]');
    this.items = [...root.querySelectorAll('.raaw-rack__item')];
    this.modal = root.querySelector('[data-rack-modal]');
    this.products = new Map();
    this.openItem = null;
    this.closeTimer = null;
    this.current = null;
    this.selected = [];

    try {
      const data = JSON.parse(root.querySelector('[data-rack-data]')?.textContent || '[]');
      data.forEach((p) => this.products.set(String(p.id), p));
    } catch (error) {
      console.warn('[raaw-rack] Product data could not be parsed', error);
    }

    this.bindRack();
    this.bindModal();
    this.observeVisibility();
    this.bindLayout();
    this.measureCollars();
  }

  /* ---------- Hanger neck length: distance from image top to the collar ---------- */

  measureCollars() {
    const canvas = document.createElement('canvas');
    const size = 120;
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return;

    const collarOf = (img) => {
      const w = img.naturalWidth;
      const h = img.naturalHeight;
      if (!w || !h) return null;
      ctx.clearRect(0, 0, size, size);
      ctx.drawImage(img, 0, 0, size, size);
      let data;
      try {
        data = ctx.getImageData(0, 0, size, size).data;
      } catch {
        return null;
      }
      const lum = (i) => data[i] + data[i + 1] + data[i + 2];
      const bg = lum(0);
      const x0 = Math.round(size * 0.3);
      const x1 = Math.round(size * 0.7);
      for (let y = 0; y < size * 0.4; y++) {
        for (let x = x0; x < x1; x++) {
          if (Math.abs(lum((y * size + x) * 4) - bg) > 18) {
            const drawn = Math.min(1, h / w);
            return (1 - drawn) / 2 + (y / size) * drawn;
          }
        }
      }
      return null;
    };

    this.items.forEach((item) => {
      [
        ['hang', item.querySelector('.raaw-rack__face--hang img')],
        ['reveal', item.querySelector('.raaw-rack__face--reveal img')],
      ].forEach(([key, img]) => {
        if (!img) return;
        const apply = () => {
          const value = collarOf(img);
          if (value !== null) item.style.setProperty(`--rack-collar-${key}`, value.toFixed(4));
        };
        if (img.complete) apply();
        else img.addEventListener('load', apply, { once: true });
      });
    });
  }

  /* ---------- Sizing: fill the rail edge to edge ---------- */

  bindLayout() {
    if (!this.board || !this.row || !this.items.length) return;
    this.layout();
    if ('ResizeObserver' in window) {
      let lastWidth = 0;
      new ResizeObserver(([entry]) => {
        const w = Math.round(entry.contentRect.width);
        if (w === lastWidth) return;
        lastWidth = w;
        this.layout();
      }).observe(this.board);
    }
    hoverQuery.addEventListener('change', () => {
      this.setOpen(null);
      this.layout();
    });
    this.row.addEventListener('scroll', () => this.queueCenter(), { passive: true });
  }

  layout() {
    const cs = getComputedStyle(this.board);
    const width = this.board.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight);
    if (width <= 0) return;
    const count = this.items.length;
    const s = this.root.style;
    const px = (v) => `${Math.round(v * 10) / 10}px`;

    if (canHover()) {
      const gMax = Math.min(500, window.innerHeight * 0.62);
      const g = clamp(width / (1 + (count - 1) * 0.3), 200, gMax);
      const closed = count > 1 ? Math.min(((width - g) / (count - 1)) * 0.98, g * 0.5) : g;
      const idle = Math.min((width / count) * 0.98, g * 0.55);
      s.setProperty('--rack-g', px(g));
      s.setProperty('--rack-open', px(g));
      s.setProperty('--rack-closed', px(closed));
      s.setProperty('--rack-closed-idle', px(idle));
      s.setProperty('--rack-tilt', tiltFor(clamp((closed * 1.08) / g, 0.15, 0.5)));
      s.setProperty('--rack-tilt-idle', tiltFor(clamp((idle * 1.05) / g, 0.15, 0.55)));
      s.setProperty('--rack-pad-inline', '0px');
    } else {
      const slot = width * 0.46;
      const g = Math.min(width * 0.7, 380);
      s.setProperty('--rack-g', px(g));
      s.setProperty('--rack-open', px(slot));
      s.setProperty('--rack-closed', px(slot));
      s.setProperty('--rack-closed-idle', px(slot));
      s.setProperty('--rack-tilt', '62deg');
      s.setProperty('--rack-tilt-idle', '62deg');
      s.setProperty('--rack-pad-inline', px((width - slot) / 2));
      requestAnimationFrame(() => this.queueCenter());
    }
  }

  queueCenter() {
    if (canHover()) return;
    cancelAnimationFrame(this.centerFrame);
    this.centerFrame = requestAnimationFrame(() => {
      const center = this.row.scrollLeft + this.row.clientWidth / 2;
      let best = null;
      let bestDist = Infinity;
      this.items.forEach((item) => {
        const dist = Math.abs(item.offsetLeft + item.offsetWidth / 2 - center);
        if (dist < bestDist) {
          bestDist = dist;
          best = item;
        }
      });
      if (best) this.setOpen(best);
    });
  }

  bindRack() {
    this.items.forEach((item) => {
      const btn = item.querySelector('[data-rack-open]');
      item.addEventListener('pointerenter', (e) => {
        if (e.pointerType === 'mouse' && canHover()) this.setOpen(item);
      });
      btn.addEventListener('focus', () => {
        if (canHover()) this.setOpen(item);
      });
      btn.addEventListener('click', () => this.openModal(btn.dataset.rackOpen, btn));
    });

    this.row?.addEventListener('pointerleave', () => {
      if (!canHover()) return;
      clearTimeout(this.closeTimer);
      this.closeTimer = setTimeout(() => this.setOpen(null), 260);
    });
    this.row?.addEventListener('pointerenter', () => clearTimeout(this.closeTimer));
    this.row?.addEventListener('focusout', (e) => {
      if (canHover() && !this.row.contains(e.relatedTarget)) this.setOpen(null);
    });
  }

  setOpen(item) {
    if (item === this.openItem) return;
    const prevIndex = this.openItem ? this.items.indexOf(this.openItem) : -1;
    this.openItem?.classList.remove('is-open');
    this.openItem = item;
    this.row?.classList.toggle('has-open', Boolean(item));
    if (!item) return;

    item.classList.add('is-open');
    const index = this.items.indexOf(item);
    const direction = prevIndex === -1 ? 0 : Math.sign(index - prevIndex);
    this.nudge(this.items[index - 1], direction >= 0 ? 'left' : 'right');
    this.nudge(this.items[index + 1], direction <= 0 ? 'right' : 'left');
  }

  nudge(item, side) {
    if (!item) return;
    const cls = side === 'left' ? 'is-nudged-left' : 'is-nudged-right';
    item.classList.remove('is-nudged-left', 'is-nudged-right');
    void item.offsetWidth;
    item.classList.add(cls);
    clearTimeout(item._nudgeTimer);
    item._nudgeTimer = setTimeout(() => item.classList.remove(cls), 950);
  }

  observeVisibility() {
    if (!('IntersectionObserver' in window)) {
      this.root.classList.add('is-visible');
      return;
    }
    new IntersectionObserver(
      (entries) => entries.forEach((entry) => this.root.classList.toggle('is-visible', entry.isIntersecting)),
      { rootMargin: '100px 0px' }
    ).observe(this.root);
  }

  /* ---------- Modal ---------- */

  bindModal() {
    if (!this.modal) return;
    this.el = {
      img: this.modal.querySelector('[data-rack-img]'),
      thumbs: this.modal.querySelector('[data-rack-thumbs]'),
      title: this.modal.querySelector('[data-rack-title]'),
      price: this.modal.querySelector('[data-rack-amount]'),
      compare: this.modal.querySelector('[data-rack-compare]'),
      desc: this.modal.querySelector('[data-rack-desc]'),
      options: this.modal.querySelector('[data-rack-options]'),
      atc: this.modal.querySelector('[data-rack-atc]'),
      atcLabel: this.modal.querySelector('[data-rack-atc-label]'),
      status: this.modal.querySelector('[data-rack-status]'),
      link: this.modal.querySelector('[data-rack-link]'),
    };

    this.modal.querySelector('[data-rack-close]')?.addEventListener('click', () => this.closeModal());
    this.modal.addEventListener('click', (e) => {
      if (e.target === this.modal) this.closeModal();
    });
    this.modal.addEventListener('close', () => {
      document.documentElement.classList.remove('raaw-rack-lock');
      this.returnFocus?.focus({ preventScroll: true });
    });
    this.el.atc.addEventListener('click', () => this.addToCart());
  }

  openModal(id, trigger) {
    const product = this.products.get(String(id));
    if (!product || !this.modal) return;
    this.current = product;
    this.returnFocus = trigger;
    this.selected = product.hasOnlyDefault ? [...(product.variants[0]?.options || [])] : product.options.map(() => null);

    this.el.title.textContent = product.title;
    this.setPrice(product.price, product.compareAt);
    this.el.desc.textContent = product.description || '';
    this.el.link.href = product.url;
    this.setStatus('');
    this.el.atc.classList.remove('is-added', 'is-loading');

    this.renderImages(product);
    this.renderOptions(product);
    this.refreshState();

    document.documentElement.classList.add('raaw-rack-lock');
    this.modal.showModal();
  }

  closeModal() {
    if (this.modal?.open) this.modal.close();
  }

  renderImages(product) {
    const images = product.images.length ? product.images : [product.reveal];
    const first = product.reveal || images[0];
    this.el.img.src = first;
    this.el.img.alt = product.title;
    this.el.thumbs.replaceChildren();
    if (images.length < 2) return;

    images.forEach((src) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'raaw-rack-modal__thumb';
      b.setAttribute('aria-pressed', String(src === first));
      b.setAttribute('aria-label', `${product.title} görseli`);
      const im = document.createElement('img');
      im.src = src.replace(/width=\d+/, 'width=160');
      im.alt = '';
      im.loading = 'lazy';
      b.append(im);
      b.addEventListener('click', () => this.swapImage(src, b));
      this.el.thumbs.append(b);
    });
  }

  swapImage(src, btn) {
    this.el.thumbs.querySelectorAll('button').forEach((t) => t.setAttribute('aria-pressed', String(t === btn)));
    if (this.el.img.src === src) return;
    this.el.img.classList.add('is-swapping');
    setTimeout(() => {
      this.el.img.src = src;
      this.el.img.classList.remove('is-swapping');
    }, 180);
  }

  renderOptions(product) {
    this.el.options.replaceChildren();
    if (product.hasOnlyDefault) return;

    product.options.forEach((option, optIndex) => {
      const group = document.createElement('div');
      group.className = 'raaw-rack-modal__option';
      group.setAttribute('role', 'group');

      const label = document.createElement('p');
      label.className = 'raaw-rack-modal__option-label';
      label.textContent = /^(boyut|size|beden)$/i.test(option.name) ? 'Beden' : option.name;
      group.setAttribute('aria-label', label.textContent);

      const values = document.createElement('div');
      values.className = 'raaw-rack-modal__values';

      option.values.forEach((value) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'raaw-rack-modal__value';
        b.textContent = value;
        b.dataset.opt = String(optIndex);
        b.dataset.value = value;
        b.setAttribute('aria-pressed', 'false');
        b.addEventListener('click', () => {
          this.selected[optIndex] = value;
          this.refreshState();
        });
        values.append(b);
      });

      group.append(label, values);
      this.el.options.append(group);
    });
  }

  findVariant(selection) {
    if (!this.current) return null;
    return (
      this.current.variants.find((v) => v.options.every((val, i) => selection[i] === val)) || null
    );
  }

  isValueAvailable(optIndex, value) {
    return this.current.variants.some(
      (v) =>
        v.available &&
        v.options.every((val, i) => {
          if (i === optIndex) return val === value;
          return this.selected[i] === null || val === this.selected[i];
        })
    );
  }

  setPrice(price, compare) {
    if (!this.el.price) return;
    this.el.price.textContent = price || '';
    if (!this.el.compare) return;
    if (compare) {
      this.el.compare.hidden = false;
      this.el.compare.textContent = compare;
    } else {
      this.el.compare.hidden = true;
      this.el.compare.textContent = '';
    }
  }

  refreshState() {
    const product = this.current;
    if (!product) return;

    this.el.options.querySelectorAll('.raaw-rack-modal__value').forEach((b) => {
      const optIndex = Number(b.dataset.opt);
      const value = b.dataset.value;
      b.setAttribute('aria-pressed', String(this.selected[optIndex] === value));
      b.disabled = !this.isValueAvailable(optIndex, value);
    });

    const complete = this.selected.every((v) => v !== null);
    const variant = complete ? this.findVariant(this.selected) : null;
    this.variant = variant;

    if (variant) this.setPrice(variant.price, variant.compareAt);

    const atc = this.el.atc;
    if (!product.available) {
      atc.disabled = true;
      this.el.atcLabel.textContent = 'Tükendi';
    } else if (!complete) {
      atc.disabled = true;
      this.el.atcLabel.textContent = product.options.length > 1 ? 'Önce seçim yap' : 'Önce beden seç';
    } else if (!variant || !variant.available) {
      atc.disabled = true;
      this.el.atcLabel.textContent = 'Bu seçenek tükendi';
    } else {
      atc.disabled = false;
      const suffix = product.hasOnlyDefault ? '' : ` (${this.selected.join(' / ')})`;
      this.el.atcLabel.textContent = `Sepete ekle${suffix}`;
    }
  }

  setStatus(text, isError = false) {
    this.el.status.textContent = text;
    this.el.status.classList.toggle('is-error', isError);
  }

  async addToCart() {
    const variant = this.variant;
    if (!variant || !variant.available) return;
    const atc = this.el.atc;
    atc.classList.add('is-loading');
    this.setStatus('');

    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => el.dataset.sectionId)
      .filter(Boolean);

    const deferred = CartLinesUpdateEvent.createPromise();
    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: String(variant.id), quantity: 1 }],
        promise: deferred.promise,
      })
    );

    try {
      const body = { items: [{ id: variant.id, quantity: 1 }] };
      if (sectionIds.length) body.sections = sectionIds.join(',');

      const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      const json = await res.json();
      const cart = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`).then((r) => r.json());

      if (!res.ok || json.status) {
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { didError: true, items: cart.items, source: 'raaw-hanger-rack', itemCount: 1 },
        });
        throw new Error(json.description || json.message || 'Sepete eklenemedi');
      }

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          didError: false,
          items: cart.items,
          source: 'raaw-hanger-rack',
          itemCount: 1,
          productId: String(this.current.id),
          sections: json.sections,
        },
      });

      atc.classList.remove('is-loading');
      atc.classList.add('is-added');
      this.el.atcLabel.textContent = 'Sepete eklendi';
      this.setStatus(`${this.current.title} sepetine eklendi.`);
      setTimeout(() => {
        atc.classList.remove('is-added');
        this.refreshState();
      }, 1800);
    } catch (error) {
      atc.classList.remove('is-loading');
      this.setStatus(error.message || 'Bir sorun oluştu, tekrar dene.', true);
      this.refreshState();
    }
  }
}

const init = () => {
  document.querySelectorAll('[data-raaw-rack]:not([data-rack-ready])').forEach((root) => {
    root.dataset.rackReady = 'true';
    new RaawRack(root);
  });
};

init();
document.addEventListener('shopify:section:load', init);
document.addEventListener('visibilitychange', () => {
  document.body.classList.toggle('raaw-rack-paused', document.hidden);
});
